import React from "react";
import PropTypes from "prop-types";
import {
  CloudUpload,
  PictureAsPdf,
  DeleteOutline as DeleteOutlineIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

/**
 * Shared indemnity marking flow body: option picker, offline confirm, or PDF upload.
 */
export default function IndemnityMarkingDialogContent({
  indemnityOption,
  setIndemnityOption,
  selectedBondForIndemnity,
  markingBondId,
  handleMarkIndemnityOffline,
  selectedFiles,
  handleFilesSelected,
  handleClearFiles,
  handleRemoveFile,
  handleViewFile,
  handleSubmitIndemnityFiles,
  isSubmitting,
  onBackFromUpload,
}) {
  if (!indemnityOption) {
    return (
      <Stack spacing={2}>
        <Button
          variant="outlined"
          fullWidth
          size="large"
          onClick={() => setIndemnityOption("offline")}
          sx={{ textTransform: "none", py: 2 }}
        >
          <Box sx={{ textAlign: "left", width: "100%" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Submitted Offline
            </Typography>
            <Typography variant="caption" color="text.secondary">
              The indemnity was submitted through other means (mail, fax, etc.)
            </Typography>
          </Box>
        </Button>
        <Button
          variant="outlined"
          fullWidth
          size="large"
          onClick={() => setIndemnityOption("upload")}
          sx={{ textTransform: "none", py: 2 }}
        >
          <Box sx={{ textAlign: "left", width: "100%" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Submit Indemnity Document(s)
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Upload PDF document(s) for validation and submission
            </Typography>
          </Box>
        </Button>
      </Stack>
    );
  }

  if (indemnityOption === "offline") {
    return (
      <Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          Confirm that the indemnity documents have been submitted through other means (physical mail,
          email, fax, etc.) and will be processed accordingly.
        </Alert>
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 2 }}>
          <Button variant="text" onClick={() => setIndemnityOption(null)}>
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleMarkIndemnityOffline}
            disabled={markingBondId === selectedBondForIndemnity?.bond_id}
          >
            {markingBondId === selectedBondForIndemnity?.bond_id ? "Processing..." : "Confirm"}
          </Button>
        </Box>
      </Box>
    );
  }

  const isProbateUnder50k =
    selectedBondForIndemnity?.bond_type === "Probate" &&
    (Number.parseFloat(selectedBondForIndemnity?.bond_amount) || 0) <= 50000;

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        Upload Indemnity PDFs
      </Typography>
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", mb: 2 }}>
        <Button
          variant="outlined"
          component="label"
          startIcon={<CloudUpload />}
          disabled={isProbateUnder50k}
          sx={{ textTransform: "none", color: "#2563eb", borderColor: "rgba(37, 99, 235, 0.3)" }}
        >
          Choose File(s){" "}
          <input type="file" hidden accept="application/pdf" multiple onChange={handleFilesSelected} />
        </Button>
        {selectedFiles.length > 0 && (
          <Button
            variant="text"
            onClick={handleClearFiles}
            sx={{ textTransform: "none", color: "text.secondary" }}
          >
            Clear selection
          </Button>
        )}
      </Box>
      {isProbateUnder50k && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
          This Indemnity Agreement does not need to be signed or submitted to Probate Bond Pros.
        </Typography>
      )}
      {selectedFiles.length > 0 && (
        <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {selectedFiles.length} file(s) selected
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {selectedFiles.map((f, i) => {
              const sizeKb = Math.max(1, Math.round((f.size || 0) / 1024));
              return (
                <Paper
                  key={`${f.name}-${String(i)}`}
                  elevation={0}
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    border: "1px solid rgba(37, 99, 235, 0.18)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PictureAsPdf sx={{ color: "error.main" }} />
                    <Box>
                      <Typography variant="body2" sx={{ color: "text.primary" }}>
                        {f.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        {sizeKb} KB
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Tooltip title="View">
                      <IconButton size="small" onClick={() => handleViewFile(f)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove">
                      <IconButton size="small" color="error" onClick={() => handleRemoveFile(i)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </Box>
      )}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
        <Button
          variant="text"
          onClick={() => {
            setIndemnityOption(null);
            onBackFromUpload();
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmitIndemnityFiles}
          disabled={!selectedFiles.length || isSubmitting || isProbateUnder50k}
        >
          {isSubmitting ? "Validating..." : "Validate & Submit"}
        </Button>
      </Box>
    </Box>
  );
}

IndemnityMarkingDialogContent.propTypes = {
  indemnityOption: PropTypes.oneOf(["offline", "upload"]),
  setIndemnityOption: PropTypes.func.isRequired,
  selectedBondForIndemnity: PropTypes.object,
  markingBondId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  handleMarkIndemnityOffline: PropTypes.func.isRequired,
  selectedFiles: PropTypes.array.isRequired,
  handleFilesSelected: PropTypes.func.isRequired,
  handleClearFiles: PropTypes.func.isRequired,
  handleRemoveFile: PropTypes.func.isRequired,
  handleViewFile: PropTypes.func.isRequired,
  handleSubmitIndemnityFiles: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  onBackFromUpload: PropTypes.func.isRequired,
};
